import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Searchable single-select with an inline "Add <query>" action for persistent lists.
export function CreatableSelect({ options, value, onChange, onCreate, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const exact = options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const create = async () => {
    setCreating(true);
    const created = await onCreate(query.trim());
    setCreating(false);
    if (created) pick(created);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid={testId}>
          <span className={cn("truncate", !value && "text-muted-foreground")}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 opacity-50" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or type a new name..."
            className="h-10 w-full bg-transparent text-sm outline-none"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-60 overflow-y-auto py-1">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => pick(o)}
              className="flex w-full items-center px-3 py-2 text-sm hover:bg-slate-100 transition-colors"
              data-testid={`${testId}-option-${o}`}
            >
              <Check className={cn("mr-2 h-4 w-4", value === o ? "opacity-100" : "opacity-0")} />
              {o}
            </button>
          ))}
          {filtered.length === 0 && !query.trim() && <p className="px-3 py-2 text-sm text-muted-foreground">No entries yet.</p>}
          {query.trim() && !exact && (
            <button
              type="button"
              disabled={creating}
              onClick={create}
              className="flex w-full items-center px-3 py-2 text-sm text-[#1F8F88] font-medium hover:bg-teal-50 border-t transition-colors"
              data-testid={`${testId}-add-new`}
            >
              <Plus className="mr-2 h-4 w-4" />
              {creating ? "Adding..." : `Add "${query.trim()}"`}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
